import React, { useState, useEffect, useRef, createContext, useContext, ReactNode } from 'react';
import { User, AuthState } from '../types';
import { authApi } from '../services/api';
import { setUiRedesignAuthContext } from '../config/uiRedesign';
import { clearReviewRecovery } from '../utils/reviewDraftRecovery';
import { checkSession, setSessionOwner } from '../services/sessionRecovery';
import { SessionRecovery } from '../components/auth/SessionRecovery';

interface AuthContextType extends AuthState {
  login: () => void;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
// Overrides displayed identity inside a preview; AuthProvider still owns sign-in.
export const AuthDisplayProvider = AuthContext.Provider;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const useOptionalAuth = () => useContext(AuthContext);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const currentUser = useRef<User | null>(null);
  const [state, setState] = useState<AuthState>({
    user: null,
    loading: true,
    error: null,
  });

  const refreshUser = async () => {
    if (currentUser.current) { await checkSession(); return; }
    try {
      const response = await authApi.getMe();
      const user = response.data;
      currentUser.current = user;
      setSessionOwner(user.id);
      setUiRedesignAuthContext({
        role: user?.role,
        teacherId: user?.role === 'teacher' ? user.id : null,
        studentId: user?.role === 'student' ? user.id : null,
      });
      setState({
        user,
        loading: false,
        error: null,
      });
    } catch (error: any) {
      // Handle 401/403 as unauthenticated, other errors as real errors
      setUiRedesignAuthContext();
      if (error.response?.status === 401 || error.response?.status === 403) {
        setState({
          user: null,
          loading: false,
          error: null,
        });
      } else {
        setState({
          user: null,
          loading: false,
          error: 'Failed to check authentication status',
        });
      }
    }
  };

  useEffect(() => {
    // Check if we're in the middle of OAuth redirect (URL contains OAuth-related params)
    const urlParams = new URLSearchParams(window.location.search);
    const isOAuthRedirect = urlParams.has('code') || urlParams.has('state') || 
                           window.location.pathname.includes('/social/complete/');
    
    // If we're in OAuth redirect, wait longer before checking auth
    const delay = isOAuthRedirect ? 2000 : 100;
    
    const timer = setTimeout(() => {
      refreshUser();
    }, delay);
    
    return () => clearTimeout(timer);
  }, []);

  const login = () => {
    authApi.login();
  };

  const logout = async () => {
    try {
      await authApi.logout();
      currentUser.current = null;
      setSessionOwner(null);
      clearReviewRecovery();
      setUiRedesignAuthContext();
      setState({
        user: null,
        loading: false,
        error: null,
      });
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const value: AuthContextType = {
    ...state,
    login,
    logout,
    refreshUser,
  };

  return React.createElement(
    AuthContext.Provider,
    { value },
    React.createElement(SessionRecovery, null, children)
  );
};
